import {useQuery} from '@tanstack/react-query'

import {isNetworkError} from '#/lib/strings/errors'
import {STALE} from '#/state/queries'
import {type TrendsScope} from '../components/TrendsScopeTabs'
import {fetchTrends} from './fetchTrends'
import {getMockTrends, type TrendsResponse} from './trends'

/**
 * Source of truth for the Explore feed.
 *
 * Fetches **by region only** — the backend's `category` filter is intentionally
 * not used. Category filtering happens client-side (see
 * `filterTrendsResponseByCategory`) so switching chips doesn't refetch and the
 * full category list stays derivable from a single response.
 *
 * - `global` / `region` scopes hit the live W Trends service (`fetchTrends`).
 *   There is **no** mock fallback for these: a missing token or a failed
 *   request resolves to an error state so integration problems are visible.
 * - `foryou` has no backend endpoint yet, so it is skipped here and served
 *   from mock data. Remove the branch once a `foryou` endpoint exists.
 *
 * Returns the React Query result plus the resolved `TrendsResponse` (or
 * `undefined` while loading / on error).
 */

const trendsQueryKeyRoot = 'wtrends'

export function trendsQueryKey({
  scope,
  region,
}: {
  scope: TrendsScope
  region: string | null
}) {
  return [
    trendsQueryKeyRoot,
    scope,
    scope === 'region' ? region : null,
  ] as const
}

export function useTrends({
  scope,
  region,
}: {
  scope: TrendsScope
  region: string | null
}) {
  const isForYou = scope === 'foryou'

  const query = useQuery<TrendsResponse>({
    queryKey: trendsQueryKey({scope, region}),
    staleTime: STALE.MINUTES.ONE,
    queryFn: async ({signal}) => {
      // `foryou` is not wired to the backend yet — serve mock data.
      if (isForYou) {
        return getMockTrends({scope, region, category: null})
      }
      return fetchTrends({scope, region, signal})
    },
  })

  // With React Query's default `networkMode: 'online'`, an offline device
  // *pauses* the query (fetchStatus 'paused') rather than erroring — so without
  // this the UI would fall through to the generic empty state. Also treat an
  // explicit network error (e.g. a mid-flight drop) as offline.
  const isOffline =
    query.fetchStatus === 'paused' ||
    (query.isError && isNetworkError(query.error))

  return {...query, isOffline}
}
